// See EnrollmentVerification.spec.md for ENV-FR-001 through ENV-FR-007
// Step 1 table: one row per host, expanding to that host's role statuses (ENV-FR-001).
import { makeStyles } from '@material-ui/styles'
import type { FC } from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Grid from 'core/elements/grid/Grid'
import type { GridCellProps } from 'core/elements/grid/hooks/useGridRows'
import Text from 'core/elements/Text'
import colors from 'core/themes/base/colors'
import type Theme from 'core/themes/model'
// Local copy of the Hosts list role names: `roleNamesMap` in infrastructure/selectors.ts is
// not exported, so these five display names are kept in sync with it by hand (PCD-9663).
import {
  errorStatuses,
  healthyStatuses,
  roleStatusMap,
} from 'openstack/components/infrastructure/selectors'
import type { EnrollmentRun, EnrollmentSelection, HostEnrollment, HostPhase } from './model'

const ROLE_NAMES: Record<string, string> = {
  hypervisor: 'Hypervisor',
  'image-library': 'Image Library',
  'persistent-storage': 'Persistent Storage',
  'pf9-support': 'Advanced Remote Support',
  dns: 'DNS',
}

// Hypervisor first, same order the Summary step and Hosts list use.
const ROLE_ORDER = ['hypervisor', 'image-library', 'persistent-storage', 'pf9-support', 'dns']

// Which role keys this run touches, from what was picked in steps 1 to 4. ROLE_ORDER only
// ever holds these five keys, so every lookup here always hits.
const roleKeysForRun = (selection: EnrollmentSelection): string[] => {
  const picked: Record<string, boolean> = {
    hypervisor: selection.hypervisor,
    'image-library': selection.imageLibrary,
    'persistent-storage': selection.backends.length > 0,
    'pf9-support': selection.remoteSupport,
    dns: selection.dns,
  }
  return ROLE_ORDER.filter((key) => picked[key])
}

const PHASE_STATUS: Record<HostPhase, { label: string; icon: string; spin?: boolean }> = {
  preparing: { label: 'Preparing', icon: 'circle-notch', spin: true },
  applying: { label: 'Applying', icon: 'circle-notch', spin: true },
  completed: { label: 'Completed', icon: 'check' },
  failed: { label: 'Failed', icon: 'xmark' },
}

type Cell = FC<GridCellProps<HostEnrollment>>

const StatusCell: Cell = ({ item }) => {
  const classes = useStyles()
  const { label, icon, spin } = PHASE_STATUS[item.phase]
  return (
    <span className={classes.statusCell}>
      <span role="img" aria-label={label} title={label}>
        <FontAwesomeIcon solid spin={spin} className={classes[`phase_${item.phase}`]}>
          {icon}
        </FontAwesomeIcon>
      </span>
      <Text variant="body2">{label}</Text>
    </span>
  )
}

const HostCell: Cell = ({ item }) => <>{item.hostName}</>

// Role status icon, same set as the host Status column: check, X, or a spinner while waiting.
const roleIcon = (
  status: string,
): { icon: string; spin?: boolean; tone: 'green' | 'red' | 'blue' } => {
  if (healthyStatuses.includes(status)) return { icon: 'check', tone: 'green' }
  if (errorStatuses.includes(status)) return { icon: 'xmark', tone: 'red' }
  return { icon: 'circle-notch', spin: true, tone: 'blue' }
}

// ENV-FR-001: every role of the run for this host, with its display status, and the host's
// error text when it failed. `Pending` covers a role the host hasn't reported on yet.
// ENV-FR-008: Figma layout, a grey 3 column grid of cells: bold role name, then icon + status.
const RoleDetails = ({
  host,
  selection,
}: {
  host: HostEnrollment
  selection: EnrollmentSelection
}) => {
  const classes = useStyles()
  // The caller only renders this when roleKeysForRun(selection) is non-empty.
  const roleKeys = roleKeysForRun(selection)
  return (
    <div className={classes.expanded}>
      <ul className={classes.roleGrid}>
        {roleKeys.map((key) => {
          const raw = host.roles[key]
          const status = raw ? (roleStatusMap[raw] ?? raw) : 'Pending'
          const { icon, spin, tone } = roleIcon(status)
          return (
            <li key={key} className={classes.roleCell}>
              <Text variant="body2" className={classes.roleName}>
                {ROLE_NAMES[key]}
              </Text>
              <span className={classes.statusCell}>
                <FontAwesomeIcon solid spin={spin} className={classes[tone]}>
                  {icon}
                </FontAwesomeIcon>
                <Text variant="body2">{status}</Text>
              </span>
            </li>
          )
        })}
      </ul>
      {host.phase === 'failed' && host.error && (
        <Text variant="body2" className={classes.error}>
          {host.error}
        </Text>
      )}
    </div>
  )
}

export default function StepOneTable({ run }: { run: EnrollmentRun }) {
  return (
    <Grid<HostEnrollment>
      uniqueIdentifier="hostId"
      data={run.hosts}
      columns={[
        { key: 'phase', label: 'Status', CellComponent: StatusCell },
        { key: 'hostName', label: 'Host', CellComponent: HostCell },
      ]}
      label="Role Application"
      disableToolbar
      disableSorting
      hideCheckboxes
      useFloatingActionBar={false}
      renderExpandedRow={(host) =>
        roleKeysForRun(run.selection).length ? (
          <RoleDetails host={host} selection={run.selection} />
        ) : null
      }
    />
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  phase_preparing: { color: theme.palette.blue[700] },
  phase_applying: { color: theme.palette.blue[700] },
  phase_completed: { color: theme.palette.green[700] },
  phase_failed: { color: theme.palette.red[700] },
  // Bleeds over the Grid cell padding (8px 16px) so the grey fills the whole expanded row.
  // Left padding lines the roles up with the status text in the row above.
  expanded: {
    display: 'grid',
    gap: theme.spacing(1.5),
    margin: theme.spacing(-1, -2),
    padding: theme.spacing(2, 2, 2, 5),
    backgroundColor: colors.grey[100],
  },
  roleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(2, 3),
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  roleCell: {
    display: 'grid',
    gap: theme.spacing(0.5),
  },
  roleName: {
    fontWeight: 700,
  },
  green: { color: theme.palette.green[700] },
  red: { color: theme.palette.red[700] },
  blue: { color: theme.palette.blue[700] },
  error: {
    color: theme.palette.red[700],
  },
}))
